import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { default as contractsService} from '../services/contracts';
import { useAppDispatch, useAppSelector } from '@/hooks';
import contractActions from '../actions/newActions';
import { ContractDisplayTypes, Contract, IElecContract, IGasContract, ContractMapped } from '../types/contracts';
import { useEffect } from 'react';
import { useSettingsContext } from '@/components/Settings/hooks/useSettingsContext';
import saveAs from 'file-saver';
import useLangs from "@/languages/hooks/useLangs";
import { InvoiceOnlineTypes } from "../types/invoiceOnline";
import { ChangeContractNotificationField } from "../types/changeContractNotificationField";

export type GoToContract = (contractId: Contract["id"] | Contract["name"], action?: string | null) => void;
export type GoToFilteredInvoices = (contract: Contract) => void;
export type SetFilter = (filter: string) => void;

type ReturnType<T extends ContractDisplayTypes> = {
  contracts: ContractMapped[T][];
  currentContract: ContractMapped[T];
}

export const useContracts = <T extends ContractDisplayTypes>(type: T) => {
  const { t } = useTranslation();
  const Settings = useSettingsContext();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {currentLang} = useLangs();
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action");
  const applyBaixaFilter = action !== null && action !== "download";

  // Necessary because the reducer infers a type that the T generic can't access
  const discriminator = type as ContractDisplayTypes;

  const contracts = useAppSelector(state => state.contracts[discriminator].items);
  const totalContractsNum = useAppSelector(state => state.contracts[discriminator].total_items);
  const viewContract = useAppSelector(state => state.contracts[discriminator].view_contract);
  const currentContract = viewContract ? ({
    ...contracts.filter(item => item.id === viewContract)[0],
    type
  } as Contract) : undefined;
  const status = useAppSelector(state => state.contracts[discriminator].status);
  const downloadingContract = useAppSelector(state => state.contracts[discriminator].downloading_contract);
  const refCatastralState = useAppSelector(state => currentContract ? state.contracts[discriminator].refCatastral.status[currentContract.id]?.status : "uninitialized");
  const invoiceOnlineState = useAppSelector(state => currentContract ? state.contracts[discriminator].invoiceOnline.status[currentContract.id]?.status : "uninitialized");

  useEffect(() => {
    if (status === "uninitialized") {
      fetchContracts(true);
    }
  }, []);
  
  // TODO: Change this to cache the calls. To do so, the function must be changed
  // into a hook. Therefor the parameters of this function must change places.
const fetchContracts = (initial: boolean = false, page: number = 1, limit: number = 10, filter: string = '') => {
  dispatch(contractActions[type].fetchContractsRequested());

  let adjustedFilter = filter;
  if (applyBaixaFilter) {
    const stateCondition = "('state', '!=', 'baixa')";
    if (filter) {
      const existingFilterContent = filter.slice(1, -1);
      adjustedFilter = `[${stateCondition}, ${existingFilterContent}]`;
    } else {
      adjustedFilter = `[${stateCondition}]`;
    }
  }

  // This switch is here to aliviate TS inability to create a union and instead
  // intersects contractAction's electricity and gas types:
  // https://stackoverflow.com/questions/79501899/why-does-typescript-infer-an-intersection-type-instead-of-a-union-when-a
  switch(type) {
      case "electricity":
        contractsService.fetchContracts<IElecContract>({
          type,
          initial,
          offset: (page - 1) * limit,
          limit: limit,
          filter: adjustedFilter,
        }).then(res => {
          dispatch(contractActions["electricity"].receivedContracts(res.promise.data));
        });
        break;
      case "gas":
        contractsService.fetchContracts<IGasContract>({
          type,
          initial,
          offset: (page - 1) * limit,
          limit: limit,
          filter: adjustedFilter,
        }).then(res => {
          dispatch(contractActions["gas"].receivedContracts(res.promise.data));
        });
        break;
    }
  };

  const goToContract: GoToContract = (contractIdOrName, action) => {
    const contract = typeof contractIdOrName == "number" ? getContractFromId(contractIdOrName) : getContractFromName(contractIdOrName);
    if (contract) {
      dispatch(contractActions[type].viewContractSet(contract.id));
      navigate(
        t(`common:url.contracts.${type}.detail`, 
          {contractName: contract.name}
        ) + (action ? `?action=${action}` : '')
      );
    } else {
      console.error("Could not navigate to desired contract. Not found.")
    }
  };

  const goToFilteredInvoices = (contract: Contract) => {
    navigate(
      t('common:url.invoices') 
      + `?contractType=${type}&contractName=${contract?.name}`
    );
  };

  const getContractFromId = (id: number) => {
    return contracts.some((c) => c.id === id) ? contracts.filter((c) => c.id === id)[0] : undefined;
  }
  
  const getContractFromName = (name: string) => {
    return contracts.some((c) => c.name === name) ? contracts.filter((c) => c.name === name)[0] : undefined;
  }
  
  const handleContractChange = (id: number) => {
    goToContract(id);
  };

  const onUpdateNotificationEmail = async (value: ChangeContractNotificationField["value"]): Promise<boolean> => {
    return await contractsService.updateContractNotificationField({
      contractName: currentContract!.name,
      field: "email",
      value
    })
    .then((res) => {
      dispatch(contractActions[type].notificationEmailSet(value));
      return true;
    })
    .catch(err => {
      return false;
    })
  };

  const onUpdateNotificationPhone = async (value: ChangeContractNotificationField["value"]): Promise<boolean> => {
    return await contractsService.updateContractNotificationField({
      contractName: currentContract!.name,
      field: "phone",
      value
    })
    .then((res) => {
      dispatch(contractActions[type].notificationPhoneSet(value));
      return true;
    })
    .catch(err => {
      return false;
    })
  };

  const downloadContract = () => {
    if (currentContract) {
      dispatch(contractActions[type].exportContractRequested());
      if (Settings.invoices?.downloadInNewTab) {
        let newTab = window.open(t('common:url.loading'), '_blank');
        return contractsService.exportContract[type](currentContract.name, currentLang)
          .then(response => {
            if (newTab) {
              newTab.location.href = URL.createObjectURL(response.promise.data);
            }
            URL.revokeObjectURL(t('common:url'));
          })
          .finally(() => {
            dispatch(contractActions[type].exportContractDone());
          });
      } else {
        contractsService.exportContract[type](currentContract.name, currentLang)
        .then(response => {
          const filename = response.promise.headers["content-disposition"].split("=");
          saveAs(response.promise.data, filename[1]);
        })
        .finally(() => {
          dispatch(contractActions[type].exportContractDone());
        });
      }
    }
  }

  const onUpdateRefCatastral = (
    refCatastral: string,
    contractName: string,
  ) => {
    dispatch(contractActions[type].refCatastralFormSubmited());

    // Crida a l'API utilitzant el servei
    contractsService.updateRefCatastral({ refCatastral, contractName, type })
    .then((res) => {
      dispatch(contractActions[type].refCatastralChanged(refCatastral));
      dispatch(contractActions[type].refCatastralFormSuccessed());
    })
    .catch(err => {
      dispatch(contractActions[type].refCatastralFormFailed());
    })
  }

  const onUpdateInvoiceOnline = (
    sendMethod: InvoiceOnlineTypes,
    contractName: string,
  ) => {
    dispatch(contractActions[type].invoiceOnlineFormSubmited());

    // Crida a l'API utilitzant el servei
    contractsService.updateInvoiceOnline({ sendMethod, contractName, type })
    .then((res) => {
      dispatch(contractActions[type].invoiceOnlineChanged(sendMethod));
      dispatch(contractActions[type].invoiceOnlineFormSuccessed());
    })
    .catch(err => {
      dispatch(contractActions[type].invoiceOnlineFormFailed());
    })
  }

  return {
    ...(type && {
      contracts,
      currentContract
    } as ReturnType<T>),
    downloadingContract,
    invoiceOnlineState,
    refCatastralState,
    totalContractsNum,
    status,
    downloadContract,
    fetchContracts,
    goToContract,
    goToFilteredInvoices,
    getContractFromId,
    getContractFromName,
    handleContractChange,
    onUpdateNotificationEmail,
    onUpdateNotificationPhone,
    onUpdateInvoiceOnline,
    onUpdateRefCatastral,
  } 

}